import { useState } from 'react';

import {
  BOND_KIND_LABEL,
  fetchBondFile,
  fetchSigningFile,
  openLink,
  useContractSignings,
  useDiscardVendorDoc,
  useGuaranteeBonds,
} from '@/lib/contractSigning';
import { shortDateTime } from '@/lib/format';
import type { ContractSigning, GuaranteeBond, Uuid } from '@/lib/types';
import { BondSubmitModal } from '@/pages/contract/BondSubmitModal';
import { SignContractModal } from '@/pages/contract/SignContractModal';
import { VendorDocModal } from '@/pages/contract/VendorDocModal';

const STATUS: Record<ContractSigning['status'], { label: string; tone: string }> = {
  SENT: { label: '서명 요청', tone: 'badge badge-blue' },
  VENDOR_SIGNED: { label: '본사 날인 대기', tone: 'badge badge-gray' },
  SIGNED: { label: '완료', tone: 'badge badge-gray' },
  VOIDED: { label: '취소', tone: 'badge badge-gray' },
};

/**
 * 「계약」 맨 위의 전자계약 (V625 · V629).
 *
 * <p>본사가 보낸 서류와 내가 쓴 서류가 한 목록이다 — 서명할 것이 먼저, 본사 날인을 기다리는 것, 끝난 것 차례.
 * [서류 작성] 으로 선급금 포기각서 · 보증이행완료확인서 · 기성 청구서를 써서 낸다. 아래는 보증증권.
 */
export function ContractSigningSection({ siteId }: { siteId: Uuid }) {
  const { data = [] } = useContractSignings(siteId);
  const bonds = useGuaranteeBonds(siteId);
  const discard = useDiscardVendorDoc(siteId);
  const [signing, setSigning] = useState<ContractSigning | null>(null);
  const [writing, setWriting] = useState(false);
  const [bonding, setBonding] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const order = { SENT: 0, VENDOR_SIGNED: 1, SIGNED: 2, VOIDED: 3 } as const;
  const rows = [...data].sort((a, b) => order[a.status] - order[b.status]);
  const fail = (cause: unknown) => setMessage(cause instanceof Error ? cause.message : '처리하지 못했습니다.');

  const download = (row: ContractSigning, kind: 'progress' | 'signed') => {
    setMessage(null);
    fetchSigningFile(siteId, row.id, kind, true)
      .then(({ url }) => openLink(url, true))
      .catch(fail);
  };

  return (
    <section className="mb-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h2 className="text-ui-sm font-semibold">전자계약</h2>
        <span className="ml-auto flex gap-2">
          <button type="button" className="btn-secondary" onClick={() => setWriting(true)}>
            서류 작성
          </button>
          <button type="button" className="btn-secondary" onClick={() => setBonding(true)}>
            보증서 제출
          </button>
        </span>
      </div>

      {rows.length > 0 && (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={STATUS[row.status].tone}>{STATUS[row.status].label}</span>
                  <span className="truncate text-ui-md font-medium">
                    {row.title || '하도급계약서'} · {[row.trade, row.workName].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {describe(row)}
                </div>
              </div>
              {row.status === 'SENT' && (
                <>
                  {row.origin === 'VENDOR' && (
                    <button
                      type="button"
                      className="btn-secondary shrink-0"
                      disabled={discard.isPending}
                      onClick={() => {
                        if (!window.confirm('이 서류를 지울까요?')) return;
                        discard.mutate(row.id, { onError: fail });
                      }}
                    >
                      지우기
                    </button>
                  )}
                  <button type="button" className="btn-primary shrink-0" onClick={() => setSigning(row)}>
                    서명하기
                  </button>
                </>
              )}
              {row.status === 'VENDOR_SIGNED' && (
                <button type="button" className="btn-secondary shrink-0" onClick={() => download(row, 'progress')}>
                  서명본 받기
                </button>
              )}
              {row.status === 'SIGNED' && (
                <button type="button" className="btn-secondary shrink-0" onClick={() => download(row, 'signed')}>
                  받기
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {(bonds.data ?? []).length > 0 && (
        <ul className="flex flex-col gap-2">
          {(bonds.data ?? []).map((bond) => (
            <BondRow key={bond.id} siteId={siteId} bond={bond} onError={fail} />
          ))}
        </ul>
      )}

      {message && (
        <div className="text-ui-xs" style={{ color: 'var(--danger, #dc2626)' }}>
          {message}
        </div>
      )}
      {signing && (
        <SignContractModal siteId={siteId} signing={signing} onClose={() => setSigning(null)} onSigned={() => setSigning(null)} />
      )}
      {writing && (
        <VendorDocModal
          siteId={siteId}
          onClose={() => setWriting(false)}
          onCreated={(created) => {
            setWriting(false);
            setSigning(created);
          }}
        />
      )}
      {bonding && <BondSubmitModal siteId={siteId} onClose={() => setBonding(false)} />}
    </section>
  );
}

function BondRow({ siteId, bond, onError }: { siteId: Uuid; bond: GuaranteeBond; onError: (cause: unknown) => void }) {
  const status = bond.status === 'CONFIRMED' ? '확인' : bond.status === 'RETURNED' ? '반려' : '확인 대기';
  return (
    <li className="card flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={bond.status === 'RETURNED' ? 'badge badge-red' : 'badge badge-gray'}>{status}</span>
          <span className="truncate text-ui-md font-medium">
            {BOND_KIND_LABEL[bond.kind]} 보증서 · {[bond.trade, bond.workName].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
          {[bond.issuer, bond.bondNo].filter(Boolean).join(' · ')}
          {(bond.startsOn || bond.endsOn) && ` · ${bond.startsOn ?? ''} ~ ${bond.endsOn ?? ''}`}
          {bond.status === 'RETURNED' && bond.reviewNote && ` · 반려: ${bond.reviewNote}`}
        </div>
      </div>
      <button
        type="button"
        className="btn-secondary shrink-0"
        onClick={() =>
          fetchBondFile(siteId, bond.id, false)
            .then(({ url }) => openLink(url, false))
            .catch(onError)
        }
      >
        보기
      </button>
    </li>
  );
}

function describe(row: ContractSigning): string {
  const file = row.originalName ?? '';
  switch (row.status) {
    case 'SENT':
      return row.origin === 'VENDOR' ? `${file} · 작성 중` : `${file} · ${row.sentByName ?? ''} ${shortDateTime(row.sentAt)} 요청`;
    case 'VENDOR_SIGNED':
      return `${file} · ${row.signerName ?? ''} ${shortDateTime(row.vendorSignedAt ?? row.signedAt)} 서명`;
    case 'SIGNED':
      return `${file} · ${shortDateTime(row.sealedAt ?? row.signedAt)} 완료`;
    default:
      return file;
  }
}
